/**
 * `diagram/layout.ts` — deterministic auto-layout for nodes without a saved position
 * (interactive-diagrams-v0.1.md §5.1 `layout.ts`).
 *
 * When a flowchart has no sidecar (or a node has no `pos` override), the canvas still needs
 * coordinates to render. This module computes a simple **layered** layout: nodes are placed in
 * ranks along the flow direction (edges point from lower to higher rank), spread across the
 * cross-axis within each rank. Pinned nodes (those with a sidecar position) are honored as-is
 * and excluded from the auto-grid so hand-tuned layout survives incremental additions (plan
 * §3 "new nodes auto-layout around pinned nodes").
 *
 * This is intentionally NOT dagre — it's a dependency-free, deterministic placeholder good
 * enough to render a readable graph, swappable for dagre/elk later behind this same signature.
 * Determinism matters: no `Math.random`, stable ordering by document appearance, so a diagram
 * lays out identically every render until the user drags something.
 */

import type { DiagramModel, DiagramEdge, NodeShape, Point, SidecarData } from './model';

export interface LayoutOptions {
  /** Empty separation between adjacent ranks, ON TOP of each rank's own extent. */
  rankGap?: number;
  /** Empty separation between adjacent nodes within a rank, ON TOP of each node's extent. */
  nodeGap?: number;
}

// Gaps are now *separation on top of measured extent* (not center-to-center), so wide labels
// can't overlap the way the old fixed 140/180 center spacing let them.
const DEFAULTS: Required<LayoutOptions> = { rankGap: 84, nodeGap: 44 };

/** True if the direction lays out top-to-bottom / bottom-to-top (vs. left-right). */
function isVertical(direction: string): boolean {
  return direction === 'TB' || direction === 'TD' || direction === 'BT';
}

/**
 * The fraction of a tile's inner width actually usable for text, per shape. A rectangle gives all
 * of it; a diamond (rhombus) only a small central band, a hexagon/circle less than a rectangle —
 * so the SAME label needs a WIDER tile in those shapes. Without this, following the canonical
 * guidance (a `{decision}` diamond) wrapped labels mid-word inside the too-narrow shape (WS3.1/L2).
 */
const SHAPE_TEXT_FRAC: Record<NodeShape, number> = {
  none: 1,
  rectangle: 1,
  rounded: 1,
  subroutine: 0.9,
  stadium: 0.88,
  cylinder: 0.9,
  parallelogram: 0.8,
  parallelogram_alt: 0.8,
  trapezoid: 0.72,
  trapezoid_alt: 0.72,
  rhombus: 0.58,
  hexagon: 0.74,
  circle: 0.7,
  asymmetric: 0.85,
};

/**
 * Estimate a node's rendered `[w, h]` from its label + shape. Mirrors the canvas tile estimator
 * (`initialWidth/Height`) and the SVG export, so the auto-layout reserves the same footprint the
 * node actually occupies. **Word-aware** (never breaks a word — the tile widens to fit the longest
 * word) and **shape-aware** (a diamond/hexagon/circle gets a wider tile because its usable text
 * band is narrower than its bounding box). Single source: imported by `DiagramCanvas` + the export.
 */
export function estimateNodeSize(label: string, shape: NodeShape): Point {
  const CHAR = 13 * 0.6; // ~avg glyph advance at 13px
  const SPACE = CHAR;
  const PAD_X = 30;
  const PAD_Y = 16;
  const LINE_H = 19;
  const MIN_W = 72;
  const MIN_H = 40;
  const MAX_W = 280; // matches the tile CSS max-width
  const frac = SHAPE_TEXT_FRAC[shape] ?? 1;

  const words = label.split(/\s+/).filter(Boolean);
  const wordW = (s: string): number => s.length * CHAR;
  const longestWord = words.length ? Math.max(...words.map(wordW)) : CHAR;
  const oneLineText = words.reduce((sum, wd, i) => sum + (i ? SPACE : 0) + wordW(wd), 0) || CHAR;

  // Pick a tile WIDTH. It must be wide enough that the longest word fits within the shape's usable
  // text band (`minTileForWord`) so text never breaks mid-word. Low-frac shapes (diamond, hexagon,
  // circle, trapezoid) stay compact and wrap multi-word labels to 2–3 lines rather than ballooning
  // to a flat one-line box; rectangles go wide (one line) up to MAX_W.
  const shapeMaxTile = frac < 0.8 ? 190 : MAX_W;
  const minTileForWord = longestWord / frac + PAD_X;
  const oneLineTile = oneLineText / frac + PAD_X;
  const w = Math.round(
    Math.min(MAX_W, Math.max(MIN_W, minTileForWord, Math.min(oneLineTile, shapeMaxTile))),
  );

  // Word-aware wrap at the tile's actual usable band → line count.
  const usable = (w - PAD_X) * frac;
  let lines = 1;
  let cur = 0;
  for (const wd of words) {
    const ww = wordW(wd);
    if (cur === 0) cur = ww;
    else if (cur + SPACE + ww <= usable) cur += SPACE + ww;
    else {
      lines += 1;
      cur = ww;
    }
  }

  const textH = lines * LINE_H + PAD_Y;
  // Diamond + circle also shrink the VERTICAL text band → a taller tile holds the same lines.
  const vfrac = shape === 'rhombus' || shape === 'circle' ? frac : 1;
  let h = Math.max(MIN_H, Math.round(textH / vfrac));
  let ww = w;
  if (shape === 'circle') {
    const d = Math.max(ww, h, 88);
    ww = d;
    h = d;
  }
  return [ww, h];
}

/** Extra separation between subgraph blocks — leaves room for each frame's header + padding. */
const GROUP_GAP_EXTRA = 56;

/**
 * Size-aware longest-path layered layout for a set of ids. Ranks along the flow axis; within a
 * rank, nodes are laid end-to-end by their cross extent (+`nodeGap`). Returns top-left positions
 * **normalized so the tightest bounding box starts at (0,0)**, plus that box's `[w, h]`. Pure —
 * reused both for a single subgraph's members and for the quotient graph of blocks.
 */
function layeredLayout(
  ids: string[],
  edges: readonly DiagramEdge[],
  direction: string,
  sizeOf: (id: string) => Point,
  rankGap: number,
  nodeGap: number,
): { pos: Map<string, Point>; w: number; h: number } {
  const pos = new Map<string, Point>();
  if (ids.length === 0) return { pos, w: 0, h: 0 };

  const vertical = isVertical(direction);
  const alongExtent = (id: string): number => (vertical ? sizeOf(id)[1] : sizeOf(id)[0]);
  const crossExtent = (id: string): number => (vertical ? sizeOf(id)[0] : sizeOf(id)[1]);

  const ranks = rankNodes(ids, edges);
  const byRank = new Map<number, string[]>();
  for (const id of ids) {
    const r = ranks.get(id) ?? 0;
    const bucket = byRank.get(r);
    if (bucket) bucket.push(id);
    else byRank.set(r, [id]);
  }

  let alongCursor = 0;
  for (const [, bucket] of [...byRank.entries()].sort((a, b) => a[0] - b[0])) {
    const rankThickness = Math.max(...bucket.map(alongExtent));
    const totalCross =
      bucket.reduce((sum, id) => sum + crossExtent(id), 0) + (bucket.length - 1) * nodeGap;
    let crossCursor = -totalCross / 2;
    for (const id of bucket) {
      const [w, h] = sizeOf(id);
      const alongCenter = alongCursor + rankThickness / 2;
      const crossCenter = crossCursor + crossExtent(id) / 2;
      const cx = vertical ? crossCenter : alongCenter;
      const cy = vertical ? alongCenter : crossCenter;
      pos.set(id, [cx - w / 2, cy - h / 2]);
      crossCursor += crossExtent(id) + nodeGap;
    }
    alongCursor += rankThickness + rankGap;
  }

  // Normalize so the bounding box starts at (0,0); return its extent.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [id, [x, y]] of pos) {
    const [w, h] = sizeOf(id);
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w);
    maxY = Math.max(maxY, y + h);
  }
  for (const [id, [x, y]] of pos) pos.set(id, [x - minX, y - minY]);
  return { pos, w: maxX - minX, h: maxY - minY };
}

/**
 * Compute positions for every node in the model. Nodes present in `sidecar.pos` keep those
 * coordinates; the rest are auto-placed. Returns a map from node id to `[x, y]` (React Flow
 * top-left) covering *all* model nodes.
 *
 * The auto-placement is **size-aware** (each rank is as thick as its largest node, nodes are laid
 * end-to-end by their own extent so long labels never overlap) AND **subgraph-clustered**: every
 * `subgraph`'s members are laid out together as one block, then the blocks (plus each ungrouped
 * node as its own singleton block) are placed by the same layered algorithm on the quotient graph
 * of edges between blocks. This keeps a subgraph's tiles physically together, so the canvas frame
 * drawn around them is tight — matching the grouping the vanilla mermaid render shows. With no
 * subgraphs, every node is its own block and this reduces to the plain size-aware layered layout.
 */
export function computeLayout(
  model: DiagramModel,
  sidecar: SidecarData | null = model.sidecar,
  options: LayoutOptions = {},
): Map<string, Point> {
  const opts = { ...DEFAULTS, ...options };
  const positions = new Map<string, Point>();

  // 1. Honor pinned nodes.
  const pinned = new Set<string>();
  if (sidecar) {
    for (const [id, p] of Object.entries(sidecar.pos)) {
      if (model.nodes.has(id)) {
        positions.set(id, [p[0], p[1]]);
        pinned.add(id);
      }
    }
  }

  const autoIds = [...model.nodes.keys()].filter((id) => !pinned.has(id));
  if (autoIds.length === 0) return positions;

  const sizeOf = (id: string): Point => {
    const s = sidecar?.size?.[id];
    if (s) return s;
    const n = model.nodes.get(id);
    return n ? estimateNodeSize(n.label, n.shape) : [96, 40];
  };

  // 2. Assign each auto node to a block: its subgraph (first that lists it) or its own singleton.
  const blockOf = new Map<string, string>();
  for (const sg of model.subgraphs) {
    for (const m of sg.members) {
      if (!pinned.has(m) && model.nodes.has(m) && !blockOf.has(m)) blockOf.set(m, `sg:${sg.id}`);
    }
  }
  for (const id of autoIds) if (!blockOf.has(id)) blockOf.set(id, `n:${id}`);

  // Blocks in first-appearance order (deterministic).
  const blockIds: string[] = [];
  const blockMembers = new Map<string, string[]>();
  for (const id of autoIds) {
    const b = blockOf.get(id)!;
    const bucket = blockMembers.get(b);
    if (bucket) bucket.push(id);
    else {
      blockMembers.set(b, [id]);
      blockIds.push(b);
    }
  }

  // 3. Lay out each block internally; record its local positions + extent.
  const localPos = new Map<string, Map<string, Point>>();
  const blockSize = new Map<string, Point>();
  for (const b of blockIds) {
    const { pos, w, h } = layeredLayout(
      blockMembers.get(b)!,
      model.edges,
      model.direction,
      sizeOf,
      opts.rankGap,
      opts.nodeGap,
    );
    localPos.set(b, pos);
    blockSize.set(b, [w, h]);
  }

  // 4. Place the blocks via the same algorithm on the quotient graph (edges crossing blocks).
  //    Extra gap leaves room for each subgraph frame's header + padding.
  const qEdges: DiagramEdge[] = model.edges
    .filter((e) => {
      const bf = blockOf.get(e.from);
      const bt = blockOf.get(e.to);
      return bf !== undefined && bt !== undefined && bf !== bt;
    })
    .map(
      (e): DiagramEdge => ({
        from: blockOf.get(e.from)!,
        to: blockOf.get(e.to)!,
        ordinal: 0,
        connector: 'solid',
        arrow: 'arrow',
        key: '',
      }),
    );
  const blockPlacement = layeredLayout(
    blockIds,
    qEdges,
    model.direction,
    (b) => blockSize.get(b) ?? [96, 40],
    opts.rankGap + GROUP_GAP_EXTRA,
    opts.nodeGap + GROUP_GAP_EXTRA,
  );

  // 5. Compose: final position = block placement + local position within the block.
  for (const b of blockIds) {
    const [bx, by] = blockPlacement.pos.get(b) ?? [0, 0];
    for (const [id, [lx, ly]] of localPos.get(b)!) {
      positions.set(id, [Math.round(bx + lx), Math.round(by + ly)]);
    }
  }

  return positions;
}

/**
 * Assign a longest-path rank to each node. Roots (no incoming edge within the set) get rank 0;
 * every other node's rank is 1 + max(rank of its predecessors). Cycles are handled by capping
 * iterations at the node count (a back-edge simply doesn't push a node deeper forever).
 */
function rankNodes(ids: string[], edges: readonly DiagramEdge[]): Map<string, number> {
  const idSet = new Set(ids);
  const preds = new Map<string, string[]>();
  for (const id of ids) preds.set(id, []);
  for (const e of edges) {
    if (e.from === e.to) continue; // self-loop doesn't affect ranking
    if (idSet.has(e.from) && idSet.has(e.to)) preds.get(e.to)!.push(e.from);
  }

  const rank = new Map<string, number>();
  for (const id of ids) rank.set(id, 0);

  // Relax ranks up to |V| times (Bellman-Ford-style longest path on a DAG; cycle-safe cap).
  for (let iter = 0; iter < ids.length; iter += 1) {
    let changed = false;
    for (const id of ids) {
      const ps = preds.get(id)!;
      let best = 0;
      for (const p of ps) best = Math.max(best, (rank.get(p) ?? 0) + 1);
      if (best !== rank.get(id)) {
        rank.set(id, best);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return rank;
}
